import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import studentsAPI from '../../api/studentsAPI';

export default function AddStudentModal({ 
  isOpen, 
  onClose, 
  classId, 
  periods = [], 
  activePeriodId, 
  onNewStudentCreated,
  onExistingStudentsEnrolled
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { currentSchool } = useTenant();
  
  // Modal state
  const [studentModalTab, setStudentModalTab] = useState('existing');
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loadingAvailableStudents, setLoadingAvailableStudents] = useState(false);
  const [selectedStudentsToEnroll, setSelectedStudentsToEnroll] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [enrollmentOption, setEnrollmentOption] = useState('current');
  const [isEnrollingStudents, setIsEnrollingStudents] = useState(false);
  const [isCreatingStudent, setIsCreatingStudent] = useState(false);
  
  // New student form
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });

  // Load available students when modal opens
  useEffect(() => {
    if (!isOpen || !classId || !currentSchool?.id) {
      return;
    }
    
    const loadAvailableStudents = async () => {
      setLoadingAvailableStudents(true);
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://api.arcattend.com",
            scope: 'openid profile email',
          },
        });

        // Get all students in the school
        const allStudentsResponse = await studentsAPI.getStudentsBySchool(currentSchool.id, {}, token);
        
        // Handle different response formats
        let allStudents = [];
        if (Array.isArray(allStudentsResponse)) {
          allStudents = allStudentsResponse;
        } else if (allStudentsResponse && Array.isArray(allStudentsResponse.students)) {
          allStudents = allStudentsResponse.students;
        } else if (allStudentsResponse && Array.isArray(allStudentsResponse.data)) {
          allStudents = allStudentsResponse.data;
        }
        
        // Get students enrolled in the current period (not the entire class)
        let enrolledInPeriodStudents = [];
        if (activePeriodId) {
          const enrolledInPeriodResponse = await studentsAPI.getStudentsByPeriod(classId, activePeriodId, token);
          
          // Handle different response formats for period students
          if (Array.isArray(enrolledInPeriodResponse)) {
            enrolledInPeriodStudents = enrolledInPeriodResponse;
          } else if (enrolledInPeriodResponse && Array.isArray(enrolledInPeriodResponse.students)) {
            enrolledInPeriodStudents = enrolledInPeriodResponse.students;
          } else if (enrolledInPeriodResponse && Array.isArray(enrolledInPeriodResponse.data)) {
            enrolledInPeriodStudents = enrolledInPeriodResponse.data;
          }
        }
        
        const enrolledInPeriodIds = new Set(enrolledInPeriodStudents.map(s => s.id || s.student_id));
        
        // Filter out students already enrolled in the current period
        const available = allStudents.filter(student => {
          const studentId = student.id || student.student_id;
          const isEnrolledInPeriod = enrolledInPeriodIds.has(studentId);
          return !isEnrolledInPeriod;
        });
        
        setAvailableStudents(available);
      } catch (error) {
        console.error('❌ AddStudentModal: Error loading available students:', error);
        setAvailableStudents([]);
      } finally {
        setLoadingAvailableStudents(false);
      }
    };

    loadAvailableStudents();
  }, [isOpen, classId, currentSchool, activePeriodId, getAccessTokenSilently]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStudentModalTab('existing');
      setSelectedStudentsToEnroll([]);
      setStudentSearchQuery('');
      setEnrollmentOption('current');
      setNewStudent({ first_name: '', last_name: '', email: '' });
      setIsEnrollingStudents(false);
      setIsCreatingStudent(false);
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    setSelectedStudentsToEnroll([]);
    setStudentSearchQuery('');
    setNewStudent({ first_name: '', last_name: '', email: '' });
    setIsEnrollingStudents(false);
    setIsCreatingStudent(false);
    onClose();
  };

  const handleEnrollExistingStudents = async () => {
    if (selectedStudentsToEnroll.length === 0) return;

    setIsEnrollingStudents(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.arcattend.com",
          scope: 'openid profile email',
        },
      });

      // Determine which periods to enroll in
      let periodIds;
      if (enrollmentOption === 'current' && activePeriodId) {
        periodIds = [activePeriodId];
      } else if (enrollmentOption === 'all') {
        periodIds = periods.map(p => p.id);
      } else {
        periodIds = []; // No period enrollment
      }

      // Extract student IDs
      const studentIds = selectedStudentsToEnroll.map(student => student.id || student.student_id);

      console.log('🎓 Starting bulk enrollment process:', {
        studentsCount: selectedStudentsToEnroll.length,
        studentIds,
        periodsCount: periodIds.length,
        enrollmentOption,
        classId,
        periodIds
      });

      // Use bulk enrollment API
      const bulkResult = await studentsAPI.bulkEnrollStudents({
        student_ids: studentIds,
        classroom_id: classId,
        period_ids: periodIds
      }, token);

      console.log('🎓 Bulk enrollment result:', bulkResult);

      // Check for any failures
      if (bulkResult.failed_enrollments && bulkResult.failed_enrollments.length > 0) {
        const failedStudents = bulkResult.failed_enrollments.map(f => 
          `Student ID ${f.student_id}: ${f.error}`
        ).join('\n');
        
        console.warn('⚠️ Some enrollments failed:', failedStudents);
        
        // Show warning but continue if some succeeded
        if (bulkResult.successful_enrollments.length > 0) {
          alert(`Warning: ${bulkResult.successful_enrollments.length} students enrolled successfully, but ${bulkResult.failed_enrollments.length} failed:\n\n${failedStudents}`);
        } else {
          throw new Error(`All enrollments failed:\n${failedStudents}`);
        }
      }

      // Show info about skipped enrollments
      if (bulkResult.skipped_enrollments && bulkResult.skipped_enrollments.length > 0) {
        console.info('ℹ️ Some students were already enrolled:', bulkResult.skipped_enrollments);
      }

      // Notify parent component with the enrolled students
      if (onExistingStudentsEnrolled) {
        // Pass only the students that were enrolled in the current period
        if (enrollmentOption === 'current' && activePeriodId) {
          // Format students with proper period_id for immediate display
          const studentsWithPeriodId = selectedStudentsToEnroll.map(student => ({
            ...student,
            period_id: activePeriodId,
            // Ensure consistent ID field naming
            id: student.id || student.student_id,
            student_id: student.student_id || student.id
          }));
          console.log('🎓 Enrolling existing students - passing to parent:', studentsWithPeriodId);
          console.log('🎓 Sample student structure:', studentsWithPeriodId[0]);
          onExistingStudentsEnrolled(studentsWithPeriodId);
        } else if (enrollmentOption === 'all') {
          // Format students with proper period_id for immediate display
          const studentsWithPeriodId = selectedStudentsToEnroll.map(student => ({
            ...student,
            period_id: activePeriodId,
            // Ensure consistent ID field naming
            id: student.id || student.student_id,
            student_id: student.student_id || student.id
          }));
          console.log('🎓 Enrolling existing students (all periods) - passing to parent:', studentsWithPeriodId);
          console.log('🎓 Sample student structure:', studentsWithPeriodId[0]);
          onExistingStudentsEnrolled(studentsWithPeriodId);
        } else {
          // No students were enrolled in the current period
          onExistingStudentsEnrolled([]);
        }
      }

      console.log('🎓 Bulk enrollment completed successfully');
      handleCloseModal();
    } catch (error) {
      console.error('❌ Error enrolling students:', error);
      alert(`Failed to enroll students: ${error.message}`);
    } finally {
      setIsEnrollingStudents(false);
    }
  };

  const handleSubmitNewStudent = async (e) => {
    e.preventDefault();
    
    if (!newStudent.first_name.trim() || !newStudent.last_name.trim()) {
      alert('Please enter both first and last name');
      return;
    }

    setIsCreatingStudent(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.arcattend.com",
          scope: 'openid profile email',
        },
      });

      console.log('🆕 Creating new student:', {
        first_name: newStudent.first_name.trim(),
        last_name: newStudent.last_name.trim(),
        email: newStudent.email.trim() || undefined,
        school_id: currentSchool.id
      });

      // Create the new student
      const createdStudent = await studentsAPI.createStudent({
        first_name: newStudent.first_name.trim(),
        last_name: newStudent.last_name.trim(),
        email: newStudent.email.trim() || undefined,
        school_id: currentSchool.id
      }, token);

      console.log('🆕 Student created successfully:', createdStudent);

      // Determine which periods to enroll in
      let periodIds;
      if (enrollmentOption === 'current' && activePeriodId) {
        periodIds = [activePeriodId];
      } else if (enrollmentOption === 'all') {
        periodIds = periods.map(p => p.id);
      } else {
        periodIds = []; // No period enrollment
      }

      console.log('🆕 Enrolling new student in periods:', periodIds);

      // Use bulk enrollment for consistency (even for single student)
      if (periodIds.length > 0) {
        const bulkResult = await studentsAPI.bulkEnrollStudents({
          student_ids: [createdStudent.id],
          classroom_id: classId,
          period_ids: periodIds
        }, token);

        console.log('🆕 New student bulk enrollment result:', bulkResult);

        // Check for enrollment failures
        if (bulkResult.failed_enrollments && bulkResult.failed_enrollments.length > 0) {
          const errorMessage = bulkResult.failed_enrollments[0].error;
          throw new Error(`Failed to enroll new student: ${errorMessage}`);
        }
      }

      // Notify parent component with the new student if enrolled in current period
      if (onNewStudentCreated) {
        // Only pass the student data if they were enrolled in the current period
        if (enrollmentOption === 'current' || enrollmentOption === 'all') {
          console.log('🆕 Created new student - passing to parent:', [createdStudent]);
          onNewStudentCreated([createdStudent]); // Pass as array for consistency
        } else {
          onNewStudentCreated([]); // No students added to current period
        }
      }

      console.log('🆕 New student enrollment completed successfully');
      handleCloseModal();
    } catch (error) {
      console.error('❌ Error creating student:', error);
      alert(`Failed to create student: ${error.message}`);
    } finally {
      setIsCreatingStudent(false);
    }
  };

  if (!isOpen) return null;

  // Get current period name for display
  const currentPeriod = periods.find(p => p.id === activePeriodId);
  const currentPeriodName = currentPeriod?.name || 'No period selected';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl relative max-h-[90vh] overflow-hidden flex flex-col">
        <button
          aria-label="Close"
          onClick={handleCloseModal}
          disabled={isEnrollingStudents || isCreatingStudent}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl z-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ×
        </button>
        
        {/* Header - Fixed */}
        <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-800">Add Student to Class</h2>
          
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mt-4">
            <button
              onClick={() => setStudentModalTab('existing')}
              disabled={isEnrollingStudents || isCreatingStudent}
              className={`px-4 py-2 font-medium border-b-2 transition-colors disabled:opacity-50 ${
                studentModalTab === 'existing'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Enroll Existing Student
            </button>
            <button
              onClick={() => setStudentModalTab('new')}
              disabled={isEnrollingStudents || isCreatingStudent}
              className={`px-4 py-2 font-medium border-b-2 transition-colors disabled:opacity-50 ${
                studentModalTab === 'new'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Create New Student
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">{studentModalTab === 'existing' ? (
            /* Enroll Existing Student Tab */
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Select students from your school to enroll in this class:
              </p>
              
              {/* Search Bar */}
              {availableStudents.length > 0 && (
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={studentSearchQuery}
                    onChange={(e) => setStudentSearchQuery(e.target.value)}
                    disabled={isEnrollingStudents}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              )}
              
              {loadingAvailableStudents ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading available students...</div>
                </div>
              ) : availableStudents.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-2">No students available to enroll</div>
                  <p className="text-sm text-gray-400">
                    All students from your school are already enrolled in this class, or you may need to create a new student.
                  </p>
                </div>
              ) : (
                <>
                  {/* Selected Count and Select All */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">
                      {selectedStudentsToEnroll.length} of {availableStudents.filter(student => 
                        !studentSearchQuery || 
                        `${student.first_name} ${student.last_name}`.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                        (student.email && student.email.toLowerCase().includes(studentSearchQuery.toLowerCase()))
                      ).length} students selected
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          const filteredStudents = availableStudents.filter(student => 
                            !studentSearchQuery || 
                            `${student.first_name} ${student.last_name}`.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                            (student.email && student.email.toLowerCase().includes(studentSearchQuery.toLowerCase()))
                          );
                          setSelectedStudentsToEnroll(filteredStudents);
                        }}
                        disabled={isEnrollingStudents}
                        className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedStudentsToEnroll([])}
                        disabled={isEnrollingStudents}
                        className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Student List */}
                  <div className="h-96 overflow-y-auto border rounded-lg bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
                      {availableStudents
                        .filter(student => 
                          !studentSearchQuery || 
                          `${student.first_name} ${student.last_name}`.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
                          (student.email && student.email.toLowerCase().includes(studentSearchQuery.toLowerCase()))
                        )
                        .map((student) => {
                          const isSelected = selectedStudentsToEnroll.some(s => 
                            (s.id || s.student_id) === (student.id || student.student_id)
                          );
                          
                          return (
                            <div
                              key={student.id || student.student_id}
                              onClick={() => {
                                if (isEnrollingStudents) return;
                                if (isSelected) {
                                  setSelectedStudentsToEnroll(prev => 
                                    prev.filter(s => (s.id || s.student_id) !== (student.id || student.student_id))
                                  );
                                } else {
                                  setSelectedStudentsToEnroll(prev => [...prev, student]);
                                }
                              }}
                              className={`p-4 rounded-lg transition-all border ${
                                isEnrollingStudents 
                                  ? 'cursor-not-allowed opacity-60' 
                                  : 'cursor-pointer'
                              } ${
                                isSelected
                                  ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                                  : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                {/* Checkbox */}
                                <div className={`w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0 ${
                                  isSelected 
                                    ? 'bg-blue-600 border-blue-600' 
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                
                                {/* Student Info */}
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate">
                                    {student.first_name} {student.last_name}
                                  </div>
                                  {student.email && (
                                    <div className="text-sm text-gray-500 truncate">{student.email}</div>
                                  )}
                                  {student.role && (
                                    <div className="text-xs text-gray-400">{student.role}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </>
              )}
              
              {/* Enrollment Options */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Enrollment Option</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="enrollmentOption"
                      value="current"
                      checked={enrollmentOption === 'current'}
                      onChange={(e) => setEnrollmentOption(e.target.value)}
                      disabled={isEnrollingStudents}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Enroll in current period only ({currentPeriodName})
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="enrollmentOption"
                      value="all"
                      checked={enrollmentOption === 'all'}
                      onChange={(e) => setEnrollmentOption(e.target.value)}
                      disabled={isEnrollingStudents}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Enroll in all periods ({periods.length} periods)
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                  onClick={handleCloseModal}
                  disabled={isEnrollingStudents}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnrollExistingStudents}
                  disabled={selectedStudentsToEnroll.length === 0 || isEnrollingStudents}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isEnrollingStudents && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>
                    {isEnrollingStudents 
                      ? 'Enrolling...' 
                      : `Enroll ${selectedStudentsToEnroll.length} Student${selectedStudentsToEnroll.length !== 1 ? 's' : ''}`
                    }
                  </span>
                </button>
              </div>
            </div>
          ) : (
            /* Create New Student Tab */
            <form onSubmit={handleSubmitNewStudent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <input
                  type="text"
                  value={newStudent.first_name}
                  onChange={e => setNewStudent(s => ({ ...s, first_name: e.target.value }))}
                  disabled={isCreatingStudent}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter student's first name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Name</label>
                <input
                  type="text"
                  value={newStudent.last_name}
                  onChange={e => setNewStudent(s => ({ ...s, last_name: e.target.value }))}
                  disabled={isCreatingStudent}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter student's last name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email (Optional)</label>
                <input
                  type="email"
                  value={newStudent.email}
                  onChange={e => setNewStudent(s => ({ ...s, email: e.target.value }))}
                  disabled={isCreatingStudent}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter student's email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">School</label>
                <input
                  type="text"
                  value={currentSchool?.name || 'Current School'}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  placeholder="School name"
                />
                <p className="text-xs text-gray-500 mt-1">Student will be added to this school</p>
              </div>
              
              {/* Enrollment Options */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Enrollment Option</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="enrollmentOptionNew"
                      value="current"
                      checked={enrollmentOption === 'current'}
                      onChange={(e) => setEnrollmentOption(e.target.value)}
                      disabled={isCreatingStudent}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Enroll in current period only ({currentPeriodName})
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="enrollmentOptionNew"
                      value="all"
                      checked={enrollmentOption === 'all'}
                      onChange={(e) => setEnrollmentOption(e.target.value)}
                      disabled={isCreatingStudent}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Enroll in all periods ({periods.length} periods)
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                  onClick={handleCloseModal}
                  disabled={isCreatingStudent}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingStudent}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isCreatingStudent && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>
                    {isCreatingStudent ? 'Creating...' : 'Create & Enroll Student'}
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
